import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleLeft } from "@fortawesome/free-regular-svg-icons";
import moment from "moment";

import { AESDecrypt } from "../../../encrypt-util";
import { AppDispatch } from "../template/store";
import DataTable from "../../common/components/DataTable";
import {
  getSezCertReqHistory,
  getSezRejectedMessage,
  SezCertReqHistoryItem,
} from "./SezActions";

const COMPACT_BREAKPOINT = 768;

const decodeSezCertReqId = (raw?: string) => {
  if (!raw) return NaN;
  try {
    return Number(AESDecrypt(decodeURIComponent(raw)));
  } catch {
    return NaN;
  }
};

const SezHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { sez_cert_req_id } = useParams();
  const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || "";

  const resolvedSezCertReqId = decodeSezCertReqId(sez_cert_req_id);

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<SezCertReqHistoryItem[]>([]);
  const [isCompact, setIsCompact] = useState(
    typeof window !== "undefined" ? window.innerWidth < COMPACT_BREAKPOINT : false
  );

  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!resolvedSezCertReqId || Number.isNaN(resolvedSezCertReqId)) return;

    let cancelled = false;
    const loadHistory = async () => {
      setLoading(true);
      try {
        const result = await dispatch(getSezCertReqHistory(resolvedSezCertReqId)).unwrap();
        if (!cancelled) setHistory(result?.history || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(getSezRejectedMessage(e, "Failed to fetch SEZ certificate request history"));
          setHistory([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [resolvedSezCertReqId, dispatch]);

  const rows = useMemo(
    () =>
      [...(history || [])]
        .sort((a, b) => moment.utc(b.timeLine).valueOf() - moment.utc(a.timeLine).valueOf())
        .map((item, index) => ({
          id: index + 1,
          status: item.status || "-",
          createdBy: item.createdBy || "-",
          requesterId: item.requesterId || "-",
          timeLine: item.timeLine
            ? moment.utc(item.timeLine).local().format("DD MMM YYYY HH:mm:ss")
            : "-",
          remarks: item.remarks || "-",
        })),
    [history]
  );

  const remarksCell = (rowData: any) => {
    const remarks = rowData?.row?.original?.remarks || "-";
    return (
      <span
        style={{
          display: "inline-block",
          maxWidth: "100%",
          whiteSpace: "normal",
          overflowWrap: "break-word",
          wordBreak: "normal",
          lineHeight: 1.4,
        }}
        title={remarks}
      >
        {remarks}
      </span>
    );
  };

  const columnConfig = useMemo(
    () => [
      { accessor: "id", Header: "", width: 0, maxWidth: 0 },
      { accessor: "status", Header: "Status", width: isCompact ? 90 : 120, maxWidth: 140 },
      {
        accessor: "createdBy",
        Header: "Created By",
        width: isCompact ? 120 : 160,
        maxWidth: 200,
      },
      {
        accessor: "requesterId",
        Header: "Requester ID",
        width: isCompact ? 110 : 140,
        maxWidth: 180,
      },
      {
        accessor: "timeLine",
        Header: "Timeline",
        width: isCompact ? 150 : 170,
        maxWidth: 190,
      },
      {
        accessor: "remarks",
        Header: "Remarks",
        width: isCompact ? 220 : 360,
        minWidth: 200,
        maxWidth: 500,
        Cell: remarksCell,
      },
    ],
    [isCompact]
  );

  return (
    <div className="h-100 overflow-auto bg-white sez-panel-scroll">
      <style>{`
        .sez-panel-scroll {
          scrollbar-width: thin;
          scrollbar-color: #d7dde7c7 transparent;
        }
        .sez-panel-scroll::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .sez-panel-scroll::-webkit-scrollbar-thumb {
          background: #d7dde7c7;
          border-radius: 15px;
        }
        .sez-panel-scroll::-webkit-scrollbar-thumb:hover {
          background: #b7bcc4d8;
        }
        .sez-panel-scroll .table-responsive {
          scrollbar-width: thin;
          scrollbar-color: #d7dde7c7 transparent;
        }
        .sez-panel-scroll .table-responsive::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .sez-panel-scroll .table-responsive::-webkit-scrollbar-thumb {
          background: #d7dde7c7;
          border-radius: 15px;
        }
      `}</style>
      <div className="d-flex align-items-center border-bottom sticky-top bg-white py-3 px-3 flex-wrap">
        <FontAwesomeIcon
          icon={faArrowAltCircleLeft}
          className="text-warning cursor-pointer fa-lg mb-1 me-2 flex-shrink-0"
          onClick={() => navigate(`${APP_PATH_PREFIX}/sezDashboard`)}
        />
        <h2 className="fs-0 text-primary mb-0 text-truncate">{t("resource_2:Sez_history")}</h2>
      </div>

      <div className="p-3">
        <div className="table-responsive">
          <DataTable
            gridName="SEZ_CERT_HISTORY"
            columns={columnConfig}
            data={rows}
            shortPrepend="sez_hist_"
            perPage={100}
            sortable={false}
            searchable={false}
            showColumnFilter={false}
            selection={false}
            pagination={false}
            seekPagination={false}
            showExport={false}
            localFilter={false}
            uniqueFilterKey="sezCertificateHistory"
            autoHeight={true}
            maxHeight="75vh"
            tableMinHeight={200}
            staticHiddenCols={["id"]}
            dataLoading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default SezHistory;